import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { MdAddCircleOutline, MdInfoOutline } from 'react-icons/md'
import { formatDayAndMonth as shortDate } from '@/utils/dateFormat'
import ProductDetailModal from '@/components/sun/ProductDetailModal'
import { useNavigate } from 'react-router-dom'
import ProductBrowser from '@/components/products/ProductBrowser'
import { isOneOffProduct, oneOffProductPath } from '@/utils/oneOffProduct'
import type { PriceListItem, SubscriptionLine, HolidayRequest } from '@/types'
import priceListService from '@/services/priceListService'
import { howOftenLabel, type AddSchedule } from '@/utils/billingFrequency'
import subscriptionService from '@/services/subscriptionService'
import holidayService from '@/services/holidayService'
import deliveryOverlayService from '@/services/deliveryOverlayService'
import { movedDateNote, movedDeliveryTargets, schedulePeriods } from '@/utils/deliveryOverlay'
import { formatDate as longDate } from '@/utils/dateFormat'
import { useAuth } from '@/hooks/useAuth'
import { useCutoffs } from '@/hooks/useCutoffs'
import { isDeliveryWeek, neverDelivers } from '@/utils/deliverySchedule'
import { isUnknownOutcome } from '@/services/apiClient'
import {
  getDateDaysFromNow,
  getDayName,
  getWeekdayFromDateString,
  getChargebeeIdForDay,
  formatOrdinalDate,
  addDaysToDateString,
} from '@/utils/deliveryDays'
import '@/styles/components/change-items-modal.css'
import '@/styles/components/add-product-modal.css'

// Fallback when a product has no WooCommerce image (matches the calendar).
const PRODUCT_IMAGE = '/milk-bottle.jpg'

interface AddProductModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void | Promise<void>
  subscriptionId: string
  /**
   * The billing frequency of that subscription (1 weekly, 2 fortnightly...).
   * Products are offered from this frequency's price list, so a new line
   * always matches its subscription (DF-379). Defaults to weekly.
   */
  billingFrequency?: number
  /**
   * The schedules the customer could add to, one per billing frequency
   * (DF-380). With more than one, the modal first asks how often they want
   * the product and adds it to that subscription, priced from that
   * frequency. With one or none, subscriptionId and billingFrequency are used.
   */
  schedules?: AddSchedule[]
  /**
   * The SKU of initialProductId. A product id belongs to one frequency, so
   * once the customer picks a different frequency the same product is found
   * in that frequency's list by its SKU.
   */
  initialProductSku?: string
  /** The delivery the customer launched this from (its day is prefilled to 1). */
  deliveryDate?: string
  /** The customer's current lines, used to block adding a duplicate per day. */
  currentItems: SubscriptionLine[]
  /**
   * Open straight onto a product, skipping the picker. The products page
   * passes this because the tile the customer clicked already *is* step 1.
   */
  initialProductId?: string
  /**
   * Hand a product the customer already gets over to the change dialog, which
   * is where quantities and extra days are edited. Without it, picking one of
   * those here lands on a step with every day disabled.
   */
  onChangeInstead?: (productId: string) => void
  /**
   * A brand-new customer's first delivery date, while their order is still
   * being set up. The backend refuses an add starting on or before it
   * (FEDEV-544), so the modal pushes its earliest offered date past this and
   * never shows the locked day (DF-333). Null/undefined when nothing is locked.
   */
  firstDeliveryLockDate?: string | null
}

const formatPrice = (price: number): string => `£${price.toFixed(2)}`

// How far ahead to offer start dates per day.
const HORIZON_DAYS = 8 * 7

// How far a ticked day may look for its first delivery when it has none on or
// after the chosen start in the list above (DF-473: e.g. its Christmas dates
// are paused). The same reach as the change dialog.
const NEXT_DATE_HORIZON_DAYS = 26 * 7

// Run-length options, in weeks. N weeks means N deliveries on every selected
// day, whatever weekday it falls on. Longer runs are typed in instead.
const DURATION_WEEK_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const MAX_DURATION_WEEKS = 52

/**
 * Add Product wizard (FEDEV-448 redesign), three steps:
 *   1. Choose a product (the full list).
 *   2. Set a quantity per delivery day. All the customer's delivery days are
 *      shown; days the product can't be delivered on (or it's already on) are
 *      disabled. The day they launched from is prefilled to 1.
 *   3. Choose a start date for each day that has a quantity, from a dropdown of
 *      that day's valid dates.
 *
 * On confirm it makes one createSubscriptionLine call per day with a quantity
 * (the backend is per-day), reporting per-day success/failure. The delivery day
 * is derived from each chosen date, so day and date can never disagree.
 */
const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  subscriptionId,
  billingFrequency = 1,
  schedules = [],
  initialProductSku,
  deliveryDate,
  currentItems,
  initialProductId,
  onChangeInstead,
  firstDeliveryLockDate,
}) => {
  const { user } = useAuth()
  const { cutoffs } = useCutoffs()

  // Step 0 asks how often, only for customers on more than one schedule. It
  // comes after the product is picked (DF-380): browse, pick, then choose the
  // schedule, then quantities and dates.
  const [step, setStep] = useState<0 | 1 | 2 | 3>(1)
  const needsSchedule = schedules.length > 1
  const [chosen, setChosen] = useState<AddSchedule | null>(null)
  /** The product picked from the list, waiting for its schedule to be chosen. */
  const [pendingProduct, setPendingProduct] = useState<PriceListItem | null>(null)
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  const [priceListItems, setPriceListItems] = useState<PriceListItem[]>([])
  const [holidays, setHolidays] = useState<HolidayRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The product whose detail popup is open, if any. */
  const [viewing, setViewing] = useState<PriceListItem | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<PriceListItem | null>(null)
  const [quantities, setQuantities] = useState<Record<number, number>>({})
  const [startFrom, setStartFrom] = useState<string>('')
  /** How many weeks to run for. null = no end date (runs until cancelled). */
  const [durationWeeks, setDurationWeeks] = useState<number | null>(null)
  /** Whether the duration is being typed in rather than picked from the list. */
  const [customDuration, setCustomDuration] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  /** The Christmas glass-to-plastic periods, and whether this customer has
   *  arranged to keep glass (DF-473 rule 8). */
  const [glass, setGlass] = useState<{ windows: { from: string, to: string }[], optedOut: boolean }>({ windows: [], optedOut: false })
  /** DF-478: where moved deliveries (Christmas) actually arrive, and the periods. */
  const [moves, setMoves] = useState<{ targets: Map<string, string>, periods: ReturnType<typeof schedulePeriods> }>({ targets: new Map(), periods: [] })

  // The add cutoff: the earliest date a new line may start.
  const minDate = useMemo((): string => {
    const cutoffMin = cutoffs ? cutoffs.cutoffs.add_new_line.min_date : getDateDaysFromNow(1)

    // A brand-new customer's first delivery is locked while it is being set up:
    // the backend refuses an add starting on or before it (FEDEV-544). Push the
    // earliest offered date to the day after the lock so we never present a day
    // that would be rejected, which makes the first selectable date the next
    // deliverable day after the first delivery (DF-333).
    if (firstDeliveryLockDate) {
      const dayAfterLock = addDaysToDateString(firstDeliveryLockDate.slice(0, 10), 1)
      return dayAfterLock > cutoffMin ? dayAfterLock : cutoffMin
    }

    return cutoffMin
  }, [cutoffs, firstDeliveryLockDate])

  // The customer's delivery days (from CRM, falling back to their current
  // lines), 0=Sun..6=Sat.
  const customerDeliveryDays = useMemo<number[]>(() => {
    if (user?.delivery_days && user.delivery_days.length > 0) {
      return [...user.delivery_days].sort((a, b) => a - b)
    }
    const days = new Set<number>()
    currentItems
      .filter(line => !line.end_date)
      .forEach(line => {
        if (line.delivery_day !== null && line.delivery_day !== undefined) {
          days.add(line.delivery_day)
        }
      })
    return Array.from(days).sort((a, b) => a - b)
  }, [user?.delivery_days, currentItems])

  const fetchData = useCallback(async (frequency: number) => {
    try {
      setLoading(true)
      setError(null)
      const [items, holidayResp] = await Promise.all([
        priceListService.getPriceListItems(frequency),
        holidayService.getHolidays(100),
      ])
      setPriceListItems(items)
      setHolidays(holidayResp.data || [])
    } catch (err) {
      console.error('Failed to load products:', err)
      setError('Failed to load products. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  // Reset to a clean state every time the modal opens. Products are browsed
  // from the first schedule (weekly when there is one); a customer on more
  // than one schedule chooses how often after picking (DF-380).
  useEffect(() => {
    if (isOpen) {
      const start = needsSchedule
        ? schedules[0]
        : schedules.find(s => s.subscriptionId === subscriptionId) ?? { subscriptionId, frequency: billingFrequency }
      setStep(1)
      setChosen(start)
      setPendingProduct(null)
      setScheduleError(null)
      setPriceListItems([])
      setSelectedProduct(null)
      setViewing(null)
      setQuantities({})
      setStartFrom('')
      setDurationWeeks(null)
      setCustomDuration(false)
      setSubmitting(false)
      setError(null)
      fetchData(start.frequency)
      // Supporting detail only: without it the plastic note just doesn't show.
      deliveryOverlayService.getOverlay()
        .then(o => {
          setGlass({ windows: o.glass_windows, optedOut: o.glass_opted_out })
          const targets = movedDeliveryTargets(o.round_day_amendments, o.date_overrides)
          setMoves({ targets, periods: schedulePeriods(targets) })
        })
        .catch(() => undefined)
    }
    // Only on opening: the caller's props are read once, at that moment.
  }, [isOpen])

  const chosenFrequency = chosen?.frequency ?? null

  // The caller named a product, so pick it the moment the price list lands and
  // go straight to quantities. It can't be done in the open effect because the
  // list is still being fetched at that point.
  useEffect(() => {
    if (!isOpen || !initialProductId || selectedProduct || pendingProduct || step !== 1 || priceListItems.length === 0) return
    // By id, or, when the customer chose another frequency, by the same SKU.
    const item = priceListItems.find(i => i.product_id === initialProductId)
      ?? (initialProductSku ? priceListItems.find(i => i.product_number === initialProductSku) : undefined)
    if (item) handleSelectProduct(item)
  }, [isOpen, initialProductId, priceListItems, selectedProduct])

  const isDateOnHoliday = useCallback((dateString: string): boolean => {
    const date = new Date(dateString)
    return holidays.some(h => {
      if (h.statecode !== 0) return false
      return date >= new Date(h.start_date) && date <= new Date(h.end_date)
    })
  }, [holidays])

  // WooCommerce is the customer catalogue, so prefer its name; fall back to the
  // CRM name, then the product number.
  const productName = (item: PriceListItem): string => item.name || item.description || item.product_number

  // Can the selected product be delivered on this weekday at all?
  const isDeliverableOn = useCallback((day: number): boolean => {
    return !!selectedProduct && getChargebeeIdForDay(selectedProduct, day) !== null
  }, [selectedProduct])

  /**
   * Live lines per product, keyed by product id: the days it is already on and
   * the quantity on each. Same liveness rule as alreadyOnDay below.
   */
  const orderedByProduct = useMemo<Map<string, { day: number, quantity: number }[]>>(() => {
    const today = getDateDaysFromNow(0)
    const map = new Map<string, { day: number, quantity: number }[]>()

    for (const line of currentItems) {
      if (!line.product_id || line.delivery_day === null || line.delivery_day === undefined) continue
      if (neverDelivers(line)) continue
      if (line.end_date && line.end_date.slice(0, 10) <= today) continue

      const days = map.get(line.product_id) ?? []
      days.push({ day: line.delivery_day, quantity: line.quantity })
      map.set(line.product_id, days)
    }

    for (const days of map.values()) {
      days.sort((a, b) => (a.day === 0 ? 7 : a.day) - (b.day === 0 ? 7 : b.day))
    }

    return map
  }, [currentItems])

  /**
   * The soonest a new line on this day could start: its next occurrence from
   * the cutoff onwards, skipping booked holidays. This is the date the
   * duplicate test below has to compare against, because it is the date the
   * server will be asked to accept.
   */
  const soonestStartForDay = useCallback((day: number): string => {
    for (let i = 0; i < HORIZON_DAYS; i++) {
      const d = addDaysToDateString(minDate, i)
      if (getWeekdayFromDateString(d) !== day) continue
      if (isDateOnHoliday(d)) continue

      return d
    }

    return minDate
  }, [minDate, isDateOnHoliday])

  /**
   * Is the product already in the subscription on this day?
   *
   * Mirrors the backend's duplicate guard, which tests whether the existing
   * line is still running on the date the new one would start. It is NOT
   * "ends at some point in the future": a line ending in August does not
   * clash with one starting in September, and testing it that way refused
   * adds the server would have accepted.
   */
  const alreadyOnDay = useCallback((day: number): boolean => {
    if (!selectedProduct) return false
    const wouldStartOn = soonestStartForDay(day)

    return currentItems.some(line => {
      if (line.product_id !== selectedProduct.product_id) return false
      if (line.delivery_day !== day) return false
      // A line cancelled before it started never delivers, so it doesn't
      // occupy the day — same rule as the backend's duplicate guard.
      if (neverDelivers(line)) return false
      // end_date is the INCLUSIVE last delivery, so an equal date overlaps.
      return !line.end_date || line.end_date.slice(0, 10) >= wouldStartOn
    })
  }, [selectedProduct, currentItems, soonestStartForDay])

  // The days a quantity can actually be set on: deliverable and not already there.
  const addableDays = useMemo<number[]>(() => {
    return customerDeliveryDays.filter(day => isDeliverableOn(day) && !alreadyOnDay(day))
  }, [customerDeliveryDays, isDeliverableOn, alreadyOnDay])

  // Per-day list of valid start dates (weekday matches, from the cutoff onwards,
  // skipping holidays, over the horizon). A non-weekly product can only start
  // on one of its subscription's existing delivery weeks (DF-380).
  const availableDatesByDay = useMemo<Record<number, string[]>>(() => {
    const map: Record<number, string[]> = {}
    if (!selectedProduct) return map
    for (const day of customerDeliveryDays) {
      if (!isDeliverableOn(day)) { map[day] = []; continue }
      const dates: string[] = []
      for (let i = 0; i < HORIZON_DAYS; i++) {
        const d = addDaysToDateString(minDate, i)
        if (getWeekdayFromDateString(d) === day && !isDateOnHoliday(d)
          && isDeliveryWeek(d, chosen?.startDate, chosen?.frequency)) {
          dates.push(d)
        }
      }
      map[day] = dates
    }
    return map
  }, [selectedProduct, customerDeliveryDays, minDate, isDateOnHoliday, isDeliverableOn, chosen])

  const activeDays = useMemo<number[]>(
    () => customerDeliveryDays.filter(day => (quantities[day] || 0) > 0),
    [customerDeliveryDays, quantities]
  )

  // The union of valid dates across the days the customer wants, sorted. These
  // are the single "start from" options that apply to every selected day.
  const unionDates = useMemo<string[]>(() => {
    const set = new Set<string>()
    activeDays.forEach(day => (availableDatesByDay[day] || []).forEach(d => set.add(d)))
    return Array.from(set).sort()
  }, [activeDays, availableDatesByDay])

  // The chosen "start from" date: explicit pick if still valid; else the card's
  // date if it's an option; else the soonest available.
  const effectiveStartFrom = useMemo<string>(() => {
    if (startFrom && unionDates.includes(startFrom)) return startFrom
    if (deliveryDate && unionDates.includes(deliveryDate)) return deliveryDate
    return unionDates[0] || ''
  }, [startFrom, unionDates, deliveryDate])

  // Each day's actual first delivery: the first valid date for that day on or
  // after the chosen start-from date. The backend needs start_date's weekday to
  // match the delivery day, so a single start-from resolves to each day's own
  // next occurrence (e.g. "from Mon 29th" → Thursday line starts Thu 2nd).
  //
  // Never earlier than the chosen start: a day with nothing on or after it in
  // the list starts on its next available date further ahead instead (it used
  // to fall back to its last date BEFORE the start, so a Friday could quietly
  // begin the week before). Empty if there's none, which blocks the add.
  const resolvedStartForDay = useCallback((day: number): string => {
    const list = availableDatesByDay[day] || []
    const inList = list.find(d => d >= effectiveStartFrom)
    if (inList || !effectiveStartFrom || list.length === 0) return inList || ''

    for (let i = 1; i <= NEXT_DATE_HORIZON_DAYS; i++) {
      const d = addDaysToDateString(effectiveStartFrom, i)
      if (getWeekdayFromDateString(d) === day && !isDateOnHoliday(d)
        && isDeliveryWeek(d, chosen?.startDate, chosen?.frequency)) {
        return d
      }
    }
    return ''
  }, [availableDatesByDay, effectiveStartFrom, isDateOnHoliday, chosen])

  // What each selected day actually gets. Every day starts on its own next
  // occurrence, so a shared "start from" resolves differently per day; a run
  // length in weeks then gives each day the SAME number of deliveries, which a
  // single shared end date could not (it would give one weekday an extra one).
  //
  // Each day is created as its own line, so each carries its own end date: the
  // last delivery itself, since the backend's tsg_enddate is inclusive.
  const perDaySchedule = useMemo(() => {
    return activeDays.map(day => {
      const first = resolvedStartForDay(day)
      const last = first && durationWeeks
        ? addDaysToDateString(first, (durationWeeks - 1) * 7)
        : ''
      return { day, quantity: quantities[day] || 0, first, last }
    })
  }, [activeDays, resolvedStartForDay, durationWeeks, quantities])

  /**
   * DF-473 rule 8: a glass product added for any delivery in a glass-to-plastic
   * period (Christmas) comes in plastic, so say so before they confirm. It
   * still shows as the glass product everywhere else.
   */
  const deliveredInPlastic = useMemo(() => {
    if (!selectedProduct?.is_glass || glass.optedOut) return false
    return perDaySchedule.some(({ quantity, first, last }) =>
      quantity > 0 && !!first && glass.windows.some(w => w.to >= first && (!last || w.from <= last)))
  }, [selectedProduct, glass, perDaySchedule])

  const navigate = useNavigate()

  const handleSelectProduct = (item: PriceListItem) => {
    // A one-off product (milk lockers, DF-230) is bought through its own landing
    // page (2-week lead, no day choice), not this day-by-day wizard. It still
    // lists here like any product; selecting it jumps to that page.
    if (isOneOffProduct(item)) {
      onClose()
      navigate(oneOffProductPath(item))
      return
    }

    // More than one schedule: ask how often before quantities (DF-380).
    if (needsSchedule) {
      setPendingProduct(item)
      setScheduleError(null)
      setStep(0)
      return
    }

    proceedWithProduct(item)
  }

  /**
   * The customer chose a schedule for the picked product. Each schedule is its
   * own set of products in CRM, so find the same product (by SKU) in that
   * schedule's price list, then go on to quantities on that subscription.
   */
  const chooseSchedule = async (schedule: AddSchedule) => {
    if (!pendingProduct) return
    setScheduleError(null)

    let items = priceListItems
    if (schedule.frequency !== chosenFrequency) {
      try {
        setLoading(true)
        items = await priceListService.getPriceListItems(schedule.frequency)
      } catch {
        setScheduleError("We couldn't load that schedule's products. Please try again.")
        return
      } finally {
        setLoading(false)
      }
    }

    const match = items.find(i => i.product_id === pendingProduct.product_id)
      ?? items.find(i => i.product_number === pendingProduct.product_number)

    if (!match) {
      setScheduleError(`${productName(pendingProduct)} isn't available ${howOftenLabel(schedule.frequency).toLowerCase()}. Please choose another option.`)
      return
    }

    setChosen(schedule)
    setPriceListItems(items)
    setPendingProduct(null)
    proceedWithProduct(match)
  }

  const proceedWithProduct = (item: PriceListItem) => {
    // Already in the order? The change dialog owns that product: it edits the
    // quantity on the days it's on and can add the days it isn't.
    if (onChangeInstead && orderedByProduct.has(item.product_id)) {
      onChangeInstead(item.product_id)
      return
    }

    setSelectedProduct(item)
    // Seed each delivery day from the customer's actual existing quantity of
    // this product, defaulting to 0 (DF-312). We must not pre-fill a day to 1
    // just because it is available: that read as "you already get 1 here" and
    // misled customers into thinking they had an order they did not. The Next
    // button stays disabled until they set a quantity themselves (canGoToDates).
    const existingByDay = new Map<number, number>(
      (orderedByProduct.get(item.product_id) ?? []).map(({ day, quantity }) => [day, quantity])
    )
    const q: Record<number, number> = {}
    customerDeliveryDays.forEach(day => { q[day] = existingByDay.get(day) ?? 0 })
    setQuantities(q)
    setStartFrom('')
    setDurationWeeks(null)
    setCustomDuration(false)
    setStep(2)
  }

  /** Back from quantities to "how often", for the same product. */
  const backToSchedule = () => {
    setPendingProduct(selectedProduct)
    setSelectedProduct(null)
    setScheduleError(null)
    setStep(0)
  }

  /** Back from "how often" to the product list (or out, if it was preset). */
  const backFromSchedule = () => {
    setPendingProduct(null)
    setScheduleError(null)
    if (initialProductId) onClose()
    else setStep(1)
  }

  const setDayQty = (day: number, qty: number) => {
    setQuantities(prev => ({ ...prev, [day]: Math.max(0, qty) }))
  }

  /** Everything the customer could order, before the category and search narrow it. */
  const orderableItems = useMemo(() => {
    return priceListItems.filter(item => {
      if (item.end_date) return false
      // A product with no ChargeBee id on ANY day is orderable nowhere
      // (broken price-list data, BUG-12 in the backend's docs/bugs.md), so
      // showing it is a dead end: every day stepper disabled with no way
      // forward. Products that merely don't overlap the customer's own
      // delivery days stay visible with the explanatory disabled state.
      return Object.values(item.chargebee_ids ?? {}).some(Boolean)
    })
  }, [priceListItems])

  const handleConfirm = async () => {
    if (!selectedProduct || activeDays.length === 0) return

    setSubmitting(true)
    setError(null)

    const results = await Promise.allSettled(perDaySchedule.map(({ day, quantity, first, last }) => {
      const chargebeeId = getChargebeeIdForDay(selectedProduct, day)
      if (!first || !chargebeeId) {
        return Promise.reject(new Error(`No valid start date for ${getDayName(day)}`))
      }
      return subscriptionService.createSubscriptionLine({
        subscription_id: chosen?.subscriptionId ?? subscriptionId,
        product_id: selectedProduct.product_id,
        delivery_day: day,
        quantity,
        start_date: first,
        // Own end date per day, so each gets the same number of deliveries.
        ...(last ? { end_date: last } : {}),
        chargebee_id: chargebeeId,
        product_name: productName(selectedProduct),
        unit_price: selectedProduct.amount,
      }, { checksUnknownOutcome: true })
    }))

    const outcomes = results.map((r, i) => ({ r, day: activeDays[i], first: perDaySchedule[i]?.first ?? '' }))
    const succeeded = outcomes.filter(o => o.r.status === 'fulfilled')
    let failures = outcomes.filter(o => o.r.status === 'rejected')

    // A request that timed out or lost its connection may still have been
    // saved (DF-385). Re-read the order and count any day that is now there as
    // added, rather than telling the customer it failed. A day we still can't
    // confirm is reported as unknown, never as failed, so they check before
    // trying again.
    let unconfirmed: typeof failures = []
    const unknown = failures.filter(f => isUnknownOutcome((f.r as PromiseRejectedResult).reason))
    if (unknown.length > 0) {
      let lines: SubscriptionLine[] = []
      try {
        lines = (await subscriptionService.refreshSubscriptions()).flatMap(sub => sub.lines ?? [])
      } catch {
        // Can't re-read either, so none of them can be confirmed.
      }
      const isThere = (day: number, first: string) => lines.some(line =>
        line.product_id === selectedProduct.product_id
        && line.delivery_day === day
        && line.statecode !== 1
        && (line.start_date ?? '').slice(0, 10) === first)

      succeeded.push(...unknown.filter(u => isThere(u.day, u.first)))
      unconfirmed = unknown.filter(u => !isThere(u.day, u.first))
      failures = failures.filter(f => !unknown.includes(f))
    }

    if (unconfirmed.length > 0) {
      if (succeeded.length > 0) await onSuccess?.()
      setQuantities(prev => {
        const next = { ...prev }
        succeeded.forEach(s => { next[s.day] = 0 })
        unconfirmed.forEach(u => { next[u.day] = 0 })
        return next
      })
      setError('We couldn’t confirm whether your product was added. Please refresh to check your delivery before trying again.')
      setSubmitting(false)
      return
    }

    if (failures.length === 0) {
      // Awaited so "Adding..." stays until the refreshed order is on screen.
      await onSuccess?.()
      onClose()
      setSubmitting(false)
      return
    }

    // Partial (or total) failure: refresh the parent so any succeeded lines
    // appear, zero out the succeeded days (so a retry only re-attempts the
    // failures — the backend's per-day duplicate guard would reject them
    // anyway), and report which days failed and why.
    if (succeeded.length > 0) {
      await onSuccess?.()
      setQuantities(prev => {
        const next = { ...prev }
        succeeded.forEach(s => { next[s.day] = 0 })
        return next
      })
    }
    const firstReason = (failures[0].r as PromiseRejectedResult).reason
    const reasonMsg = firstReason instanceof Error ? firstReason.message : 'Please try again.'
    const failDays = failures.map(f => getDayName(f.day)).join(' and ')
    setError(
      succeeded.length > 0
        ? `Added to ${succeeded.map(s => getDayName(s.day)).join(' and ')}. Couldn't add ${failDays}: ${reasonMsg}`
        : `Couldn't add ${failDays}: ${reasonMsg}`
    )
    setSubmitting(false)
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose()
  }

  if (!isOpen) return null

  // Once a product is chosen its own row is the header, so the steps below it
  // need no title bar and no step list to say where they are.
  const productHeader = selectedProduct ? (
    <div className="sun-edit-summary">
      <img
        className="sun-edit-summary-image"
        src={selectedProduct.image_url || PRODUCT_IMAGE}
        alt=""
        onError={(e) => { e.currentTarget.src = PRODUCT_IMAGE }}
      />
      <span className="sun-edit-summary-text">
        <span className="sun-edit-summary-name">{productName(selectedProduct)}</span>
        <span className="sun-edit-summary-price">
          {formatPrice(selectedProduct.amount_inc_vat ?? selectedProduct.amount)} each
        </span>
      </span>
    </div>
  ) : null
  const canGoToDates = activeDays.length > 0
  const allActiveDaysHaveDates = unionDates.length > 0 && activeDays.every(day => !!resolvedStartForDay(day))

  return (
    <>
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container add-product-modal" role="dialog" aria-modal="true">

        {/* No title bar on any step: the picker is self-explanatory and the
            later steps use the product row as their header, so the close sits
            on the dialog's edge instead. */}
        <button
          type="button"
          className="modal-close-floating"
          onClick={onClose}
          aria-label="Close"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="modal-content">
          {loading ? (
            <div className="modal-loading">
              <div className="modal-loading-spinner" />
              <p>Loading...</p>
            </div>
          ) : error && !priceListItems.length ? (
            <div className="modal-error">
              <div className="modal-error-icon">⚠️</div>
              <p>{error}</p>
              <button className="btn-retry" onClick={() => chosenFrequency !== null && fetchData(chosenFrequency)}>Try Again</button>
            </div>
          ) : step === 0 ? (
            <div className="add-product-frequency">
              <h3 className="add-product-frequency-title">How often would you like it?</h3>
              <p className="add-product-frequency-help">
                {pendingProduct ? <strong>{productName(pendingProduct)}</strong> : 'This product'}.{' '}
                Your deliveries are on more than one schedule. Choose which one to add it to.
              </p>
              <div className="add-product-frequency-options">
                {schedules.map(schedule => (
                  <button
                    key={schedule.subscriptionId}
                    type="button"
                    className="add-product-frequency-option"
                    onClick={() => chooseSchedule(schedule)}
                  >
                    {howOftenLabel(schedule.frequency)}
                  </button>
                ))}
              </div>
              {scheduleError && <p className="add-product-frequency-error">{scheduleError}</p>}
            </div>
          ) : step === 1 ? (
            <div className="add-product-step1">
              <ProductBrowser
                items={orderableItems}
                orderedByProduct={orderedByProduct}
                actionLabel={() => 'Add'}
                onAction={handleSelectProduct}
                onViewDetail={setViewing}
              />
            </div>
          ) : step === 2 ? (
            <div className="add-product-step2">
              {productHeader}

              <p className="field-label">How many on each day?</p>

              {addableDays.length === 0 ? (
                <p className="field-error">
                  {customerDeliveryDays.length === 0
                    ? 'You have no delivery days set up, so nothing can be added yet.'
                    : `We don’t deliver this on your delivery ${customerDeliveryDays.length === 1 ? 'day' : 'days'}`
                      + ` (${customerDeliveryDays.map(getDayName).join(' and ')}).`}
                </p>
              ) : (
                <div className="add-product-day-list">
                  {customerDeliveryDays.map(day => {
                    const deliverable = isDeliverableOn(day)
                    const already = alreadyOnDay(day)
                    const disabled = !deliverable || already
                    const qty = quantities[day] || 0
                    return (
                      <div key={day} className={`add-product-day-row ${disabled ? 'disabled' : ''}`}>
                        <div className="add-product-day-info">
                          <span className="add-product-day-name">{getDayName(day)}</span>
                          {!deliverable && (
                            <span className="add-product-day-note">Not delivered on {getDayName(day)}s</span>
                          )}
                          {deliverable && already && (
                            <span className="add-product-day-note">Already in your {getDayName(day)} delivery</span>
                          )}
                        </div>
                        {disabled ? (
                          <span className="add-product-day-unavailable">Unavailable</span>
                        ) : (
                          <div className="quantity-control">
                            <button
                              className="qty-btn"
                              onClick={() => setDayQty(day, qty - 1)}
                              disabled={qty <= 0}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              className="qty-input"
                              min={0}
                              value={qty}
                              onChange={(e) => setDayQty(day, parseInt(e.target.value) || 0)}
                            />
                            <button
                              className="qty-btn"
                              onClick={() => setDayQty(day, qty + 1)}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="add-product-step3">
              {productHeader}

              <label className="field-label" htmlFor="add-product-start-from">Start from</label>
              {unionDates.length === 0 ? (
                <p className="field-error">No available dates in the next 8 weeks.</p>
              ) : (
                <>
                  <select
                    id="add-product-start-from"
                    className="add-product-date-select"
                    value={effectiveStartFrom}
                    onChange={(e) => setStartFrom(e.target.value)}
                  >
                    {unionDates.map(d => (
                      <option key={d} value={d}>{formatOrdinalDate(d)}</option>
                    ))}
                  </select>

                  <label className="field-label" htmlFor="add-product-duration">Keep delivering for</label>
                  <select
                    id="add-product-duration"
                    className="add-product-date-select"
                    value={customDuration ? 'custom' : (durationWeeks === null ? '' : String(durationWeeks))}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === 'custom') {
                        setCustomDuration(true)
                        setDurationWeeks(w => w ?? DURATION_WEEK_OPTIONS.length)
                      } else {
                        setCustomDuration(false)
                        setDurationWeeks(v === '' ? null : parseInt(v, 10))
                      }
                    }}
                  >
                    <option value="">No end date</option>
                    {DURATION_WEEK_OPTIONS.map(w => (
                      <option key={w} value={w}>{w === 1 ? '1 week' : `${w} weeks`}</option>
                    ))}
                    <option value="custom">A set number of weeks...</option>
                  </select>

                  {customDuration && (
                    <input
                      type="number"
                      className="add-product-weeks-input"
                      min={1}
                      max={MAX_DURATION_WEEKS}
                      value={durationWeeks ?? ''}
                      aria-label="Number of weeks"
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10)
                        setDurationWeeks(Number.isNaN(n) ? null : Math.min(Math.max(n, 1), MAX_DURATION_WEEKS))
                      }}
                    />
                  )}

                  {/* Boxed like the change dialog's outcome summaries, and in
                      the same blue: it states what will happen, which is the
                      job every information banner in the app does. */}
                  <div className="add-product-start-summary sun-change-preview">
                    <MdAddCircleOutline className="sun-change-preview-icon" aria-hidden="true" />
                    <div className="sun-change-preview-body">
                    {durationWeeks === 1
                      ? <p>A one-off delivery:</p>
                      : durationWeeks
                        ? <p>We’ll deliver every week for {durationWeeks} weeks:</p>
                        : <p>We’ll deliver every week until you tell us to stop:</p>}
                    <div className="add-product-schedule">
                      {perDaySchedule.map(({ day, quantity, first, last }) => {
                        // No delivery on or after the start for this day: say
                        // so (the add is blocked) rather than start earlier.
                        if (!first) {
                          return (
                            <div className="add-product-schedule-line" key={day}>
                              No {getDayName(day)} delivery available from {shortDate(effectiveStartFrom)}
                            </div>
                          )
                        }
                        const when = last === first
                          ? shortDate(first)
                          : last
                            ? `${shortDate(first)} until ${shortDate(last)}`
                            : `from ${shortDate(first)}`
                        return (
                          <div className="add-product-schedule-line" key={day}>
                            {quantity} on {getDayName(day)}s, {when}
                          </div>
                        )
                      })}
                    </div>
                    </div>
                  </div>

                  {/* DF-478: a one-off for a date whose delivery has moved
                      (Christmas) says when it actually comes. Longer adds are
                      left as they are. */}
                  {durationWeeks === 1 && perDaySchedule
                    .map(({ quantity, first }) => (quantity > 0 && first ? movedDateNote(first, moves.targets, moves.periods, longDate, true) : null))
                    .filter((note, i, all): note is string => !!note && all.indexOf(note) === i)
                    .map(note => (
                      <p key={note} className="schedule-move-note" role="note">{note}</p>
                    ))}

                  {deliveredInPlastic && (
                    <div className="add-product-plastic-note" role="note">
                      <MdInfoOutline className="add-product-plastic-note-icon" aria-hidden="true" />
                      <p>
                        Over Christmas, products normally supplied in glass bottles will be delivered in plastic
                        bottles instead. If you&apos;d prefer to receive glass bottles only, please contact our
                        Customer Service team to arrange this.
                      </p>
                    </div>
                  )}
                </>
              )}

              {error && <p className="field-error">{error}</p>}
            </div>
          )}
        </div>

        {!loading && (
          <div className={`modal-footer add-product-footer${step <= 1 ? ' is-start' : ''}`}>
            {step === 0 ? (
              <button className="btn-cancel" onClick={backFromSchedule}>
                {initialProductId ? 'Cancel' : 'Back'}
              </button>
            ) : step === 1 ? (
              <button className="btn-cancel" onClick={onClose}>Cancel</button>
            ) : step === 2 ? (
              <>
                {/* Back to the schedule choice when there was one; otherwise to
                    the product list, or out if the product was picked for us. */}
                <button
                  className="btn-cancel"
                  onClick={() => (needsSchedule ? backToSchedule() : initialProductId ? onClose() : setStep(1))}
                >
                  {initialProductId && !needsSchedule ? 'Cancel' : 'Back'}
                </button>
                <button
                  className="btn-confirm"
                  onClick={() => setStep(3)}
                  disabled={!canGoToDates}
                >
                  Next
                </button>
              </>
            ) : (
              <>
                <button className="btn-cancel" onClick={() => setStep(2)} disabled={submitting}>Back</button>
                <button
                  className="btn-confirm"
                  onClick={handleConfirm}
                  disabled={submitting || activeDays.length === 0 || !allActiveDaysHaveDates}
                >
                  {submitting ? (
                    <>
                      <span className="btn-spinner" />
                      Adding...
                    </>
                  ) : (
                    'Add to my deliveries'
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Detail for a product being considered. No action button: the row behind
        it already adds, so the popup is here to answer "what is this?". */}
    <ProductDetailModal
      productId={viewing?.product_id ?? null}
      fallbackName={viewing ? productName(viewing) : null}
      unitPrice={viewing ? (viewing.amount_inc_vat ?? viewing.amount) : null}
      deliverableDays={viewing
        ? customerDeliveryDays.filter(day => getChargebeeIdForDay(viewing, day) !== null)
        : undefined}
      customerDeliveryDays={customerDeliveryDays}
      onClose={() => setViewing(null)}
    />
    </>
  )
}

export default AddProductModal
